import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const NREMTPrintForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    examinerName: '',
    candidateName: '',
    candidateID: '',
    examDate: new Date().toLocaleDateString(),
    startTime: '',
    endTime: '',
    totalScore: 0,
    passed: false
  });

  // Load assessment template
  useEffect(() => {
    const loadTemplate = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'nremtAssessments', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Reconstruct the template data
          setTemplate({
            name: data.name || '',
            assessmentType: data.assessmentType || 'Practical Skill',
            timeLimit: data.timeLimit || '15',
            rows: data.rows || []
          });
        } else {
          console.error('Assessment not found');
        }
      } catch (error) {
        console.error('Error loading assessment:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [id]);

  // Calculate total possible points
  const calculateTotalPoints = () => {
    if (!template?.rows) return 0;
    return template.rows.reduce((total, row) => {
      if (row.type === 'regular' && row.pointsPossible) {
        return total + parseInt(row.pointsPossible || 0);
      }
      return total;
    }, 0);
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle score changes for individual rows
  const handleScoreChange = (rowIndex, value) => {
    const updatedRows = [...template.rows];
    if (updatedRows[rowIndex]) {
      updatedRows[rowIndex].scoreAwarded = value;
      setTemplate(prev => ({
        ...prev,
        rows: updatedRows
      }));
      
      // Recalculate total score
      const totalScore = updatedRows.reduce((total, row) => {
        if (row.type === 'regular' && row.scoreAwarded) {
          return total + parseInt(row.scoreAwarded || 0);
        }
        return total;
      }, 0);
      
      setFormData(prev => ({
        ...prev,
        totalScore,
        passed: totalScore >= (calculateTotalPoints() * 0.7) // 70% passing threshold
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Assessment Not Found</h2>
          <button
            onClick={() => navigate('/nremt-dashboard')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print-specific CSS */}
      <style>
        {`
          @media print {
            /* Remove all non-essential elements */
            .no-print {
              display: none !important;
            }
            
            /* Ensure proper page layout */
            @page {
              size: 8.5in 11in;
              margin: 0.5in 0.75in;
            }
            
            /* Reset body for print */
            body {
              background: white !important;
              font-size: 11pt;
              line-height: 1.3;
              font-family: Arial, sans-serif;
            }
            
            /* Page break controls */
            .page-break {
              page-break-after: always;
            }
            
            .page-break-inside-avoid {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            .page-break-before {
              page-break-before: always;
            }
            
            /* Table styling for print */
            table {
              border-collapse: collapse;
              width: 100%;
              font-size: 9pt;
              margin-bottom: 10px;
            }
            
            th, td {
              border: 1px solid #333;
              padding: 3px 5px;
              text-align: left;
              vertical-align: top;
              word-wrap: break-word;
            }
            
            th {
              background-color: #f0f0f0 !important;
              font-weight: bold;
              text-align: center;
            }
            
            /* Header styling */
            .assessment-header {
              text-align: center;
              margin-bottom: 15px;
              page-break-inside: avoid;
            }
            
            .assessment-title {
              font-size: 14pt;
              font-weight: bold;
              margin-bottom: 8px;
              text-transform: uppercase;
            }
            
            /* Form fields styling */
            .form-field {
              border-bottom: 1px solid #333;
              min-height: 16px;
              display: inline-block;
              min-width: 120px;
              padding: 1px 3px;
              font-size: 10pt;
            }
            
            /* Score boxes */
            .score-box {
              border: 1px solid #333;
              width: 25px;
              height: 16px;
              display: inline-block;
              text-align: center;
              vertical-align: middle;
              font-size: 9pt;
            }
            
            /* Signature lines */
            .signature-line {
              border-bottom: 1px solid #333;
              min-width: 150px;
              height: 16px;
              display: inline-block;
              margin: 0 8px;
            }
            
            /* Section headers */
            .section-header {
              font-size: 12pt;
              font-weight: bold;
              margin: 12px 0 8px 0;
              border-bottom: 2px solid #333;
              padding-bottom: 3px;
              text-transform: uppercase;
            }
            
            /* Ensure content fits on page */
            .print-container {
              max-width: 100%;
              margin: 0;
              padding: 0;
            }
            
            /* Grid layouts for print */
            .print-grid {
              display: table;
              width: 100%;
              table-layout: fixed;
            }
            
            .print-grid-item {
              display: table-cell;
              padding: 3px 8px 3px 0;
              vertical-align: top;
            }
            
            /* Compact spacing */
            .compact-section {
              margin-bottom: 12px;
            }
            
            /* Row styling for different types */
            .header-row {
              background-color: #e0e0e0 !important;
              font-weight: bold;
              text-align: center;
              font-size: 10pt;
            }
            
            .sub-header-row {
              background-color: #f0f0f0 !important;
              font-weight: bold;
              font-style: italic;
              font-size: 9pt;
            }
            
            .description-row {
              background-color: #f8f8f8 !important;
              font-style: italic;
              font-size: 9pt;
            }
            
            /* Comments section */
            .comments-box {
              border: 1px solid #333;
              min-height: 60px;
              padding: 5px;
              background-color: #fafafa;
            }
            
            /* Summary styling */
            .summary-box {
              border: 2px solid #333;
              padding: 8px;
              background-color: #f5f5f5;
              text-align: center;
              font-weight: bold;
            }
          }
          
          /* Screen-only styles */
          @media screen {
            .print-preview {
              max-width: 8.5in;
              min-height: 11in;
              margin: 20px auto;
              padding: 0.5in 0.75in;
              background: white;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
              border: 1px solid #ddd;
              font-family: Arial, sans-serif;
            }
            
            .section-header {
              font-size: 14pt;
              font-weight: bold;
              margin: 15px 0 10px 0;
              border-bottom: 2px solid #333;
              padding-bottom: 5px;
              text-transform: uppercase;
            }
          }
        `}
      </style>
      
      {/* Screen controls */}
      <div className="no-print bg-gray-100 min-h-screen">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/nremt-dashboard')}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Dashboard
                </button>
                <h1 className="text-xl font-bold text-gray-900">Print Assessment Form</h1>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => window.print()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                  </svg>
                  Print Form
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Print preview container */}
        <div className="py-8">
          <div className="print-preview print-container">
            
            {/* Assessment Header */}
            <div className="assessment-header page-break-inside-avoid">
              <div className="assessment-title">{template.name}</div>
              <div style={{ fontSize: '11pt', marginBottom: '12px' }}>
                Assessment Type: {template.assessmentType} | Time Limit: {template.timeLimit} minutes
              </div>
            </div>

            {/* Candidate Information Section */}
            <div className="page-break-inside-avoid compact-section">
              <h3 className="section-header">Candidate Information</h3>
              
              <div className="print-grid">
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>Examiner Name:</label>
                  <input
                    type="text"
                    value={formData.examinerName}
                    onChange={(e) => handleFormChange('examinerName', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>Exam Date:</label>
                  <input
                    type="date"
                    value={formData.examDate}
                    onChange={(e) => handleFormChange('examDate', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
              </div>
              
              <div className="print-grid">
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>Candidate Name:</label>
                  <input
                    type="text"
                    value={formData.candidateName}
                    onChange={(e) => handleFormChange('candidateName', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>Candidate ID:</label>
                  <input
                    type="text"
                    value={formData.candidateID}
                    onChange={(e) => handleFormChange('candidateID', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
              </div>
              
              <div className="print-grid">
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>Start Time:</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleFormChange('startTime', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
                <div className="print-grid-item" style={{ width: '50%' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '10pt' }}>End Time:</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleFormChange('endTime', e.target.value)}
                    className="form-field"
                    style={{ width: '90%' }}
                  />
                </div>
              </div>
            </div>

            {/* Assessment Content */}
            <div className="page-break-inside-avoid compact-section">
              <h3 className="section-header">Assessment Criteria</h3>
              
              <table style={{ width: '100%', marginBottom: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>#</th>
                    <th style={{ width: '46%' }}>Item</th>
                    <th style={{ width: '35%' }}>Objective/Criteria</th>
                    <th style={{ width: '8%' }}>Critical</th>
                    <th style={{ width: '7%' }}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {template.rows.map((row, index) => {
                    if (row.type === 'header') {
                      return (
                        <tr key={row.id || index} className="header-row">
                          <td colSpan="5" style={{ 
                            padding: '6px 8px',
                            textAlign: 'center'
                          }}>
                            {row.text}
                          </td>
                        </tr>
                      );
                    }
                    
                    if (row.type === 'sub-header') {
                      return (
                        <tr key={row.id || index} className="sub-header-row">
                          <td colSpan="5" style={{ 
                            padding: '4px 8px'
                          }}>
                            {row.text}
                          </td>
                        </tr>
                      );
                    }
                    
                    if (row.type === 'description') {
                      return (
                        <tr key={row.id || index} className="description-row">
                          <td colSpan="5" style={{ 
                            padding: '4px 8px'
                          }}>
                            {row.text}
                          </td>
                        </tr>
                      );
                    }
                    
                    if (row.type === 'regular') {
                      return (
                        <tr key={row.id || index}>
                          <td style={{ textAlign: 'center', fontSize: '9pt' }}>{index + 1}</td>
                          <td style={{ fontSize: '9pt' }}>{row.task || row.text || ''}</td>
                          <td style={{ fontSize: '9pt' }}>{row.criteria || row.objective || ''}</td>
                          <td style={{ textAlign: 'center', fontSize: '9pt' }}>
                            {row.critical ? '●' : ''}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <input
                                type="number"
                                min="0"
                                max={row.pointsPossible || 1}
                                value={row.scoreAwarded || ''}
                                onChange={(e) => handleScoreChange(index, e.target.value)}
                                className="score-box"
                                style={{ marginBottom: '1px' }}
                              />
                              <div style={{ fontSize: '7pt', lineHeight: '1' }}>
                                / {row.pointsPossible || 1}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    
                    return null;
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary Section */}
            <div className="page-break-inside-avoid compact-section">
              <h3 className="section-header">Assessment Summary</h3>
              
              <div className="summary-box" style={{ marginBottom: '10px' }}>
                <div className="print-grid">
                  <div className="print-grid-item" style={{ width: '50%' }}>
                    <span style={{ fontSize: '11pt' }}>
                      Total Score: <strong style={{ fontSize: '13pt' }}>{formData.totalScore} / {calculateTotalPoints()}</strong>
                    </span>
                  </div>
                  <div className="print-grid-item" style={{ width: '50%' }}>
                    <span style={{ fontSize: '11pt' }}>
                      Result: <strong style={{ 
                        fontSize: '13pt',
                        color: formData.passed ? '#22c55e' : '#ef4444'
                      }}>
                        {formData.passed ? 'PASS' : 'NEEDS IMPROVEMENT'}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="page-break-inside-avoid compact-section">
              <h3 className="section-header">Signatures</h3>
              
              <div style={{ marginBottom: '25px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '10pt', marginRight: '8px' }}>Examiner Signature:</span>
                  <span className="signature-line"></span>
                  <span style={{ fontWeight: 'bold', fontSize: '10pt', marginLeft: '15px', marginRight: '8px' }}>Date:</span>
                  <span className="signature-line" style={{ minWidth: '100px' }}></span>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '10pt', marginRight: '8px' }}>Candidate Signature:</span>
                  <span className="signature-line"></span>
                  <span style={{ fontWeight: 'bold', fontSize: '10pt', marginLeft: '15px', marginRight: '8px' }}>Date:</span>
                  <span className="signature-line" style={{ minWidth: '100px' }}></span>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="page-break-inside-avoid compact-section">
              <h3 className="section-header">Examiner Comments</h3>
              
              <div className="comments-box">
                <textarea
                  rows="4"
                  style={{ 
                    width: '100%', 
                    border: 'none', 
                    backgroundColor: 'transparent',
                    resize: 'none',
                    outline: 'none',
                    fontSize: '10pt',
                    lineHeight: '1.3'
                  }}
                  placeholder="Additional comments, feedback, or recommendations..."
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default NREMTPrintForm;
